import { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

const TAXI_ENTRIES = [
  { name: 'Rab’s cabs', area: 'Penicuik', tel: '+447541256473', display: '07541 256 473' },
  { name: 'FM Taxi', area: 'Penicuik', tel: '+441968677370', display: '01968 677 370' },
  { name: 'Capital cars', area: 'Edinburgh', tel: '+441317777777', display: '0131 777 7777' },
  { name: 'ABW Cabs', area: 'Midlothian', tel: '+441316633232', display: '0131 663 3232' },
  {
    name: 'Fountain Private Hire',
    area: 'Loanhead',
    tel: '+441314403896',
    display: '0131 440 3896',
  },
];

/**
 * Local cab numbers — same chrome as {@link HotelsModal} / {@link StayTodoModal}.
 * @param {{ open: boolean; onClose: () => void; dialogId: string; faqMarkerSrc: string; tapeArtSrc: string; paperTextureSrc: string }} props
 */
export function TaxisModal({ open, onClose, dialogId, faqMarkerSrc, tapeArtSrc, paperTextureSrc }) {
  const titleId = useId();
  const closeBtnRef = useRef(null);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = window.setTimeout(() => closeBtnRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(t);
    };
  }, [open, onKeyDown]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="hotels-modal" role="presentation">
      <button
        type="button"
        className="hotels-modal__backdrop"
        aria-label="Close local cabs"
        onClick={onClose}
      />
      <div className="hotels-modal__panel-shell">
        <div className="hotels-modal__tape" aria-hidden="true">
          <img src={tapeArtSrc} alt="" className="hotels-modal__tape-img" decoding="async" />
        </div>
        <div
          id={dialogId}
          className="hotels-modal__panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <button
            ref={closeBtnRef}
            type="button"
            className="hotels-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <span aria-hidden="true">×</span>
          </button>
          <div
            className="hotels-modal__body hotels-modal__body--taxis"
            style={{
              '--hotels-modal-texture': `url("${new URL(paperTextureSrc, window.location.href).href}")`,
            }}
          >
            <div className="hotels-modal__intro-shell hotels-modal__intro-shell--taxis">
              <div className="hotels-modal__intro">
                <h2 id={titleId} className="hotels-modal__title hotels-modal__title--text">
                  Local cabs
                </h2>
                <p className="hotels-modal__taxis-lead">
                  {"Please book taxis in advance. Hogmanay is the busiest night of the year."}
                </p>
              </div>
            </div>

            <h3 className="hotels-modal__subhead hotels-modal__subhead--decorated hotels-modal__subhead--taxis">
              <span className="hotels-modal__subhead-row">
                <img
                  src={faqMarkerSrc}
                  alt=""
                  className="hotels-modal__faq-marker"
                  width="16"
                  height="5"
                  decoding="async"
                  aria-hidden="true"
                />
                <span className="hotels-modal__subhead-text">Numbers</span>
                <img
                  src={faqMarkerSrc}
                  alt=""
                  className="hotels-modal__faq-marker hotels-modal__faq-marker--flip"
                  width="16"
                  height="5"
                  decoding="async"
                  aria-hidden="true"
                />
              </span>
            </h3>

            <ul className="hotels-modal__taxi-list">
              {TAXI_ENTRIES.map((row) => (
                <li key={row.tel} className="hotels-modal__taxi">
                  <div className="hotels-modal__taxi-name">{row.name}</div>
                  <div className="hotels-modal__taxi-area">{row.area}</div>
                  <a href={`tel:${row.tel}`} className="hotels-modal__taxi-tel hotels-modal__inline-link">
                    {row.display}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
